/**
 * The question box: where a question is typed, the mode it asks in, and the one button that
 * changes meaning mid-answer.
 */

import type { JSX, RefObject } from 'react'

import { useEffect, useRef } from 'react'
import { MAX_QUESTION_LENGTH } from './api'
import { t } from './i18n'
import { ModeToggle, type Mode } from './mode'

/**
 * Where the question is typed, and the one button that changes meaning mid-answer.
 *
 * Enter sends and Shift+Enter breaks the line, which is what a chat surface has taught
 * everyone to expect. The length cap is applied here as well as at the API so that pasting
 * a whole chapter fills the textarea rather than producing a failed answer.
 */
/**
 * The one control that changes meaning mid-answer.
 *
 * Its own component because the two states are two different buttons — one
 * submits the form, one aborts a request — and reading them as a ternary
 * inside the composer hid that they are not the same control wearing two
 * labels.
 */
function SendOrStop({
  busy,
  canSend,
  onStop,
}: {
  busy: boolean
  canSend: boolean
  onStop: () => void
}): JSX.Element {
  if (busy) {
    return (
      <button type="button" onClick={onStop}>
        {t('chat.stop')}
      </button>
    )
  }
  return (
    <button type="submit" disabled={!canSend}>
      {t('chat.send')}
    </button>
  )
}

/**
 * A reader who arrives from a passage page's "Ask" finds the reference already in the
 * box and nothing sent — a question can cost them, so they write it — and the cursor
 * waiting after it. Once, on mount: the box is filled by `useHandedOffRequest`, and a
 * focused controlled textarea keeps its caret at the end when its value arrives.
 */
function useFocusWhenHandedAQuestion(): RefObject<HTMLTextAreaElement | null> {
  const box = useRef<HTMLTextAreaElement>(null)
  useEffect(() => {
    if (new URLSearchParams(window.location.search).has('q')) box.current?.focus()
  }, [])
  return box
}

interface ComposerProps {
  draft: string
  busy: boolean
  mode: Mode
  /** Whether this corpus offers research mode at all. */
  offerResearch: boolean
  grammar: string | null
  onDraftChange: (draft: string) => void
  onModeChange: (mode: Mode) => void
  /** Called with the trimmed question. The composer clears itself. */
  onAsk: (question: string) => void
  onStop: () => void
}

export function Composer({
  draft,
  busy,
  mode,
  offerResearch,
  grammar,
  onDraftChange,
  onModeChange,
  onAsk,
  onStop,
}: ComposerProps): JSX.Element {
  const box = useFocusWhenHandedAQuestion()

  // The form event, the trim and the "not while one is in flight" guard all belong here
  // rather than in the shell: they are facts about this control, not about the app.
  function ask(): void {
    const question = draft.trim()
    if (!question || busy) return
    onDraftChange('')
    onAsk(question)
  }

  return (
    <form
      className="composer"
      onSubmit={(event) => {
        event.preventDefault()
        ask()
      }}
    >
      {/* Above the question rather than beside the send button: it decides what
          kind of answer is being asked for, so it belongs where the reader is
          before they type, not next to the control that commits. */}
      {offerResearch && <ModeToggle mode={mode} grammar={grammar} onChange={onModeChange} />}

      <div className="composer__row">
      <textarea
        ref={box}
        value={draft}
        onChange={(event) => onDraftChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault()
            ask()
          }
        }}
        placeholder={t('chat.placeholder')}
        rows={2}
        aria-label={t('chat.placeholder')}
        maxLength={MAX_QUESTION_LENGTH}
      />
      <SendOrStop busy={busy} canSend={draft.trim() !== ''} onStop={onStop} />
      </div>
    </form>
  )
}
